/**
 * Cœur du player Live School (live synchrone).
 *
 * - Synchronise l'horloge sur le serveur (delta) puis dérive l'état LOCALEMENT
 *   chaque seconde (getQuizState) — aucun WebSocket, position déterministe.
 * - Pré-télécharge le payload CHIFFRÉ, récupère la clé de l'étape courante via
 *   `live-key` (libérée à l'heure), déchiffre la question (AES-256-CBC, Web Crypto).
 * - Soumet les réponses à `submit-live-answer` (le serveur est seul juge).
 *
 * Résolution d'une étape (révélation du corrigé + popup de feedback) : dès que
 * TOUT LE MONDE a répondu (résolution anticipée) OU à la fin du chrono. Le
 * décompte de participation vient des bots simulés (mode test) ou de la RPC
 * `get_live_step_participation` (vrais défis).
 *
 * Toutes les fonctions crypto/temps viennent de `@jeraisonne/db/live` (mêmes
 * helpers purs testés que le serveur).
 */

import { publishProgress } from '@/lib/progress-sync';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  aesCbcDecrypt,
  computeClockDelta,
  getQuizState,
  hexToBytes,
  stepDurationAt,
  stepOffsetMs,
  type LiveQuizStatus,
} from '@jeraisonne/db/live';
import { computeXpReward, type XpReward } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useLiveStepParticipation, type StepParticipation } from '@/lib/live-step-participation';
import { LIVE_BOT_COUNT, countBotsAnswered, planBotOffsets, positionAmong } from './bot-simulation';
import { planLiveResume, type ResumedLiveAnswer } from './live-resume';
import { privateLiveHeartbeat } from '@/lib/private-live';
import { isSupersededError, useLiveSessionLock } from './use-live-session-lock';

// Une étape se résout (corrigé + popup) quand TOUT LE MONDE a répondu ou quand
// le temps de réponse est écoulé — jamais avant : sinon on voyait le résultat et
// son classement pendant que les autres réfléchissaient encore. Défi privé
// (0687) : chaque question finit par une pause « résultats » (`result_pause_ms`),
// la question suivante ne vient qu'après.

interface LiveStateResponse {
  server_time: number;
  level: string;
  challenge_id?: string;
  title?: string | null;
  status?: LiveQuizStatus;
  starts_at?: string;
  step_duration_ms?: number;
  /** Durée de chaque question (défi privé, 0651) ; null = durée fixe. */
  step_durations_ms?: number[] | null;
  step_count?: number;
  tolerance_ms?: number;
  /** Défi privé (0687) : pause « résultats » à la fin de chaque question (ms). */
  result_pause_ms?: number | null;
  encrypted_payload?: string | null;
  reading_ms?: number;
  cover_url?: string | null;
  challenge?: null;
  /** Live School du dimanche (0329) : classement en HORA. */
  scoring?: 'hora' | 'points';
  max_score?: number | null;
  prize?: LivePrize | null;
  /** Mode test : réponses déjà données (reprise après être sorti de la page). */
  answers?: ResumedLiveAnswer[];
  /** Défi privé (0650) : hôte, code, en attente du départ manuel. */
  private?: LivePrivateInfo | null;
}

/** Défi privé (0650). */
export interface LivePrivateInfo {
  host_id: string;
  join_code: string;
  awaiting_host: boolean;
}

/** Lot d'un live de programme : SKILLCARD gagnée par le top N. */
export interface LivePrize {
  card_id: string;
  title: string;
  rarity: string;
  top_n: number;
  color_theme?: string | null;
  creature?: unknown;
  levels?: string[];
}

export interface LiveChoice {
  id: string;
  label_md: string;
}
/** Bloc de contexte (situation / énoncé intermédiaire) accumulé avant la question. */
export interface LiveContentBlock {
  content_md: string;
  /** Visuels V2 (passés à VisualBlocksRenderer). */
  visuals?: unknown[];
}
export interface LiveClue {
  id: string;
  source_text: string;
  revealed_md: string;
  xp_reward: number;
}
export interface LiveClueHunt {
  intro_md: string;
  clues: LiveClue[];
}
export interface DecryptedStep {
  /** Contexte introduit à cette étape (situation au step 0). Absent = payload legacy. */
  intro?: LiveContentBlock[];
  sub_statement_md?: string;
  prompt_md: string;
  prompt_visuals?: unknown[];
  choices: LiveChoice[];
  /** 'single' (clic direct) ou 'multiple' (cases + Valider). Absent = single (legacy). */
  answer_mode?: 'single' | 'multiple';
  /** Chasse aux indices attachée à la question (optionnel). */
  clue_hunt?: LiveClueHunt;
  /** Live de programme : SKILLCARD de la question + HORA de base. */
  card?: { id: string; title: string; hora_base: number };
  /** Défi privé (0651) : question et exercice, et si elle se joue avec la feuille. */
  question_id?: string;
  exercise_id?: string;
  has_sheet?: boolean;
  /** Feuille de brouillon livrée avec la clé (modèles publiés + lignes déjà écrites). */
  sheet?: { models?: unknown; lines?: unknown } | null;
}

/** Étape révélée (déchiffrée) + son résultat, pour l'affichage accumulé. */
export interface RevealedStep {
  index: number;
  step: DecryptedStep;
  result: StepResult | null;
  /** Choix sélectionné primaire (optimiste, dès le clic) — feedback immédiat. */
  chosenBlockId: string | null;
  /** Ensemble coché (mode multiple) — feedback immédiat. */
  chosenBlockIds: string[];
  isCurrent: boolean;
  /** L'étape est RÉSOLUE (chrono écoulé OU tout le monde a répondu) → corrigé révélé. */
  revealed: boolean;
}
interface EncryptedPayload {
  v: number;
  /** Énoncé COMMUN en clair (situation) — payload v2. Affiché en lecture + persistant. */
  statement?: LiveContentBlock[];
  steps: { iv: string; ciphertext: string }[];
}

export interface StepResult {
  stepIndex: number;
  wasCorrect: boolean;
  points: number;
  eligible: boolean;
  correctBlockId: string | null;
  /** Ensemble correct complet (mode multiple) — pour surligner toutes les bonnes. */
  correctBlockIds: string[];
  chosenBlockId: string | null;
  /** Ensemble coché (mode multiple). */
  chosenBlockIds: string[];
}

export type LivePhase =
  | 'loading'
  | 'error'
  | 'no-challenge'
  | 'waiting'
  | 'reading'
  | 'live'
  | 'finished';

/** Feedback « gamy » à afficher après une réponse (popup, comme le player V2). */
export interface LiveFeedback {
  wasCorrect: boolean;
  reward: XpReward;
  /** Rang de l'élève sur CETTE question (podium), figé au moment de la révélation. */
  stepPosition?: number | null;
  /** Nombre de participants sur cette question (pour « Nᵉ / total »). */
  stepTotal?: number;
  /** Question concernée (podium de la question). */
  stepIndex?: number;
}

/** Feedback affiché à la FIN de la chasse aux indices (XP glané), avant la question. */
export interface LiveClueFeedback {
  xp: number;
}

export interface UseLiveQuiz {
  phase: LivePhase;
  /** Défi privé complet : le serveur a refusé l'entrée (0728). */
  joinRefused?: 'full' | null;
  challengeId: string | null;
  title: string | null;
  stepCount: number;
  stepDurationMs: number;
  /** Index 0-based de l'étape courante (-1 hors LIVE). */
  stepIndex: number;
  /** Temps restant (ms) : dans l'étape (LIVE) ou avant le départ (WAITING). */
  timeLeftMs: number;
  /** Question déchiffrée de l'étape courante (null tant que la clé n'est pas là). */
  currentStep: DecryptedStep | null;
  /** Toutes les étapes révélées jusqu'ici (0..courante) + résultats — affichage accumulé. */
  revealedSteps: RevealedStep[];
  /** Énoncé COMMUN (situation) en clair — affiché en lecture puis persistant. */
  commonStatement: LiveContentBlock[];
  /** Cover de la campagne (affichée pendant la lecture). */
  coverUrl: string | null;
  /** Feedback à afficher après la dernière réponse (popup), sinon null. */
  pendingFeedback: LiveFeedback | null;
  /** Feedback de fin de chasse aux indices (popup XP), sinon null. */
  clueFeedback: LiveClueFeedback | null;
  /** XP cumulé de la session (pied de la popup). */
  sessionXp: number;
  /** Ferme la popup de feedback (réponse). */
  dismissFeedback: () => void;
  /** Crédite la XP d'une chasse aux indices terminée + déclenche sa popup. */
  registerClueComplete: (xp: number) => void;
  /** Ferme la popup de fin de chasse aux indices. */
  dismissClueFeedback: () => void;
  stepLoading: boolean;
  joinedStep: number;
  eligible: boolean;
  /** Résultat de l'étape courante une fois répondue (sinon null). */
  currentResult: StepResult | null;
  /** Décompte de participation de l'étape courante (bots en test, RPC en réel). */
  stepParticipation: StepParticipation | null;
  /** L'étape courante est RÉSOLUE en avance (tout le monde a répondu avant le chrono). */
  currentResolved: boolean;
  totalScore: number;
  correctCount: number;
  /** Soumet une réponse : un id (single), un ensemble (multiple), ou null (blanc/skip). */
  submit: (chosen: string | string[] | null) => void;
  submitting: boolean;
  refetchState: () => void;
  /** Barème : HORA (live de programme) ou points (défi historique). */
  scoring: 'hora' | 'points';
  /** Lot du live (SKILLCARD à gagner), sinon null. */
  prize: LivePrize | null;
  /** Le joueur est classé (élève du niveau) ; faux pour prof / curieux / parent. */
  ranked: boolean;
  /** Défi privé (0650), sinon null. */
  privateInfo: LivePrivateInfo | null;
  /** Instant serveur estimé (ms) — salon du défi privé. */
  serverNow: number;
  /** Le défi a été ouvert dans une autre fenêtre avec ce compte (0674). */
  superseded: boolean;
  /** « Jouer ici » : cette fenêtre reprend la main. */
  takeOver: () => Promise<void>;
}

export interface UseLiveQuizOptions {
  /** Défi privé : prénom de l'invité (session anonyme). */
  guestName?: string | null;
}

export function useLiveQuiz(
  niveau: string,
  testChallengeId?: string,
  /** Un live précis (`/live/<niveau>?defi=<id>`), sinon le live courant du niveau. */
  challengeIdParam?: string,
  options: UseLiveQuizOptions = {},
): UseLiveQuiz {
  const { session } = useAuth();
  const guestName = options.guestName ?? null;
  const testMode = !!testChallengeId;

  // 1. État serveur (heure + méta + payload chiffré). Rafraîchi périodiquement
  //    pour capter la construction du payload / le passage scheduled→live.
  //    En MODE TEST (staff), `live-test` renvoie le même format mais ancré à
  //    l'instant de lancement (starts_at = ancre) → chronométrage réel à la demande.
  const stateQuery = useQuery({
    queryKey: ['live-state', niveau, testChallengeId ?? '', challengeIdParam ?? ''],
    // On arrête de poller une fois le défi TERMINÉ (l'état ne bouge plus) — sinon
    // le poll relancerait inutilement `live-test` et l'écran de résultat serait
    // instable.
    refetchInterval: (query) => {
      const d = query.state.data as (LiveStateResponse & { clientReceiveTime: number }) | undefined;
      if (!d?.starts_at) return 30_000;
      // Défi privé : le départ dépend de l'hôte → on suit de près jusqu'au départ.
      if (d.private && (d.private.awaiting_host || Date.now() < new Date(d.starts_at).getTime())) {
        return 5_000;
      }
      const qStart = new Date(d.starts_at).getTime() + (d.reading_ms ?? 0);
      const endMs =
        qStart +
        stepOffsetMs(d.step_count ?? 0, d.step_duration_ms ?? 0, d.step_durations_ms) +
        (d.tolerance_ms ?? 0);
      if (Date.now() > endMs) return false;
      // Défi privé : une question peut finir plus tôt (tout le monde a répondu,
      // migration 0655) → on relit les durées souvent.
      return d.private ? 4_000 : 30_000;
    },
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = testMode
        ? await supabase.functions.invoke<LiveStateResponse>('live-test', {
            body: { action: 'start', challenge_id: testChallengeId },
          })
        : await supabase.functions.invoke<LiveStateResponse>('live-state', {
            body: challengeIdParam
              ? { level: niveau, challenge_id: challengeIdParam }
              : { level: niveau },
          });
      if (error) throw error;
      return { ...(data as LiveStateResponse), clientReceiveTime: Date.now() };
    },
  });

  const data = stateQuery.data;
  const challengeId = data?.challenge_id ?? null;
  const baseStepMs = data?.step_duration_ms ?? 30_000;
  const stepDurationsMs = data?.step_durations_ms ?? null;
  const stepCount = data?.step_count ?? 0;
  const resultPauseMs = Math.max(0, data?.result_pause_ms ?? 0);

  // Décalage horloge serveur (ms) — appliqué à toutes les mesures de temps.
  const serverDelta = useMemo(
    () =>
      data
        ? computeClockDelta({
            serverTime: data.server_time,
            clientReceiveTime: data.clientReceiveTime,
          })
        : 0,
    [data],
  );

  // 2. Payload chiffré (une entrée par étape).
  const payload = useMemo<EncryptedPayload | null>(() => {
    if (!data?.encrypted_payload) return null;
    try {
      return JSON.parse(data.encrypted_payload) as EncryptedPayload;
    } catch {
      return null;
    }
  }, [data?.encrypted_payload]);

  // 3. Tick d'horloge (état recalculé localement).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  // Fenêtre de lecture de l'énoncé commun : les QUESTIONS démarrent à
  // starts_at + reading_ms. Avant starts_at → WAITING ; entre les deux → READING.
  const readingMs = data?.reading_ms ?? 0;
  const startsAtMs = data?.starts_at ? new Date(data.starts_at).getTime() : 0;
  const questionStartsAt = startsAtMs + readingMs;
  const serverNow = now + serverDelta;

  const live = useMemo(() => {
    if (!data?.starts_at || !challengeId) return null;
    return getQuizState({
      serverTime: serverNow,
      startsAt: questionStartsAt,
      stepDurationMs: baseStepMs,
      stepCount,
      stepDurationsMs,
    });
  }, [
    data?.starts_at,
    challengeId,
    serverNow,
    questionStartsAt,
    baseStepMs,
    stepCount,
    stepDurationsMs,
  ]);

  const beforeStart = !!data?.starts_at && serverNow < startsAtMs;
  const isReading = !!data?.starts_at && serverNow >= startsAtMs && serverNow < questionStartsAt;
  const status = live?.status ?? null;
  const stepIndex = status === 'LIVE' ? live!.stepIndex : -1;
  // Dernière étape à afficher : l'étape courante pendant le live, TOUTES les
  // étapes une fois le défi terminé (corrections de l'écran de fin).
  const lastShownIndex = status === 'FINISHED' && stepCount > 0 ? stepCount - 1 : stepIndex;

  // 4. Rejoindre le défi une seule fois. En mode test, la participation est déjà
  //    créée par `live-test start` → on saute live-join (eligible par défaut).
  const joinedRef = useRef(false);
  const [participation, setParticipation] = useState<{
    joinedStep: number;
    eligible: boolean;
    ranked: boolean;
  } | null>(null);
  // Réponses déjà enregistrées (page quittée puis rouverte pendant le live).
  const [resumeAnswers, setResumeAnswers] = useState<ResumedLiveAnswer[] | null>(null);
  // Défi privé complet (0728) : refus du serveur à l'entrée.
  const [joinRefused, setJoinRefused] = useState<'full' | null>(null);
  useEffect(() => {
    if (testMode) return;
    if (!session || !challengeId || joinedRef.current || status === 'FINISHED' || status === null) {
      return;
    }
    joinedRef.current = true;
    supabase.functions
      .invoke<{
        joined_step: number;
        eligible: boolean;
        ranked?: boolean;
        answers?: ResumedLiveAnswer[];
      }>('live-join', {
        body: guestName
          ? { challenge_id: challengeId, guest_name: guestName }
          : { challenge_id: challengeId },
      })
      .then(({ data: j, error }) => {
        if (!error && j) {
          setParticipation({
            joinedStep: j.joined_step,
            eligible: j.eligible,
            ranked: j.ranked ?? true,
          });
          // Retour sur la page pendant le live : ses réponses déjà données.
          if (j.answers?.length) setResumeAnswers(j.answers);
          return;
        }
        const ctx = (error as { context?: Response } | null)?.context;
        if (ctx && typeof ctx.json === 'function') {
          void ctx
            .json()
            .then((b: { error?: string } | null) => {
              if (b?.error === 'private_live_full') setJoinRefused('full');
            })
            .catch(() => undefined);
        }
      })
      .catch(() => {
        joinedRef.current = false;
      });
  }, [session, challengeId, status, testMode, guestName]);

  // Une seule fenêtre par compte (0674) : la dernière ouverte prend la main.
  const sessionLock = useLiveSessionLock({
    challengeId,
    enabled: !!session && !!challengeId && status !== null && status !== 'FINISHED',
  });

  // Défi privé : signal de présence toutes les 10 s (« connectés » du salon).
  const isPrivate = !!data?.private;
  useEffect(() => {
    if (!isPrivate || !session || !challengeId || status === 'FINISHED' || !participation) return;
    void privateLiveHeartbeat(challengeId).catch(() => undefined);
    const id = setInterval(() => {
      void privateLiveHeartbeat(challengeId).catch(() => undefined);
    }, 10_000);
    return () => clearInterval(id);
  }, [isPrivate, session, challengeId, status, participation]);

  // 5. Clé de l'étape courante + déchiffrement (caché par étape).
  const fetchStep = useCallback(
    async (index: number): Promise<DecryptedStep> => {
      const { data: k, error } = await supabase.functions.invoke<{
        key: string;
        sheet?: { models?: unknown; lines?: unknown } | null;
      }>('live-key', {
        body: { challenge: challengeId, step: index, test: testMode },
      });
      if (error) throw error; // too_early (skew) → retry
      if (!k?.key) throw new Error('no_key');
      const enc = payload?.steps[index];
      if (!enc) throw new Error('no_step');
      const plain = await aesCbcDecrypt(hexToBytes(k.key), enc.iv, enc.ciphertext);
      const step = JSON.parse(plain) as DecryptedStep;
      return k.sheet ? { ...step, sheet: k.sheet } : step;
    },
    [challengeId, testMode, payload],
  );
  const stepQuery = useQuery({
    queryKey: ['live-step', challengeId, stepIndex],
    enabled: !!session && !!challengeId && stepIndex >= 0 && !!payload,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: 6,
    retryDelay: 800,
    queryFn: () => fetchStep(stepIndex),
  });

  // Accumulation : on conserve chaque étape déchiffrée pour un affichage
  // cumulatif (énoncé + questions/réponses précédentes, comme le player V2).
  const [revealed, setRevealed] = useState<Record<number, DecryptedStep>>({});
  useEffect(() => {
    if (stepIndex >= 0 && stepQuery.data) {
      setRevealed((prev) => (prev[stepIndex] ? prev : { ...prev, [stepIndex]: stepQuery.data! }));
    }
  }, [stepIndex, stepQuery.data]);

  // Retour sur la page en plein live : les étapes PASSÉES (clés déjà libérées)
  // sont re-déchiffrées pour retrouver l'affichage accumulé.
  const pastFetchRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!session || !challengeId || !payload || lastShownIndex < 0) return;
    // Pendant le live : les étapes passées ; défi terminé : toutes.
    const upTo = status === 'FINISHED' ? lastShownIndex : stepIndex - 1;
    for (let i = 0; i <= upTo; i++) {
      if (revealed[i] || pastFetchRef.current.has(i)) continue;
      pastFetchRef.current.add(i);
      fetchStep(i)
        .then((step) => setRevealed((prev) => (prev[i] ? prev : { ...prev, [i]: step })))
        .catch(() => pastFetchRef.current.delete(i));
    }
  }, [session, challengeId, payload, stepIndex, lastShownIndex, status, revealed, fetchStep]);

  // 6. Soumission de réponse (serveur seul juge).
  const [results, setResults] = useState<Record<number, StepResult>>({});
  // Sélection OPTIMISTE (posée au clic/validation) → feedback immédiat, avant la
  // réponse serveur. Ensemble coché (vide = blanc).
  const [selected, setSelected] = useState<Record<number, string[]>>({});
  // Garde SYNCHRONE anti-double-clic (toute soumission, y compris les blancs
  // auto). Le state est async → un double-tap rapide le contournerait.
  const answeredRef = useRef<Set<number>>(new Set());
  /** Temps de réponse écoulé (pause « résultats », défi privé) : plus d'envoi. */
  const answerTimeOverRef = useRef(false);
  // Étapes RÉELLEMENT répondues par l'élève (≠ blancs auto) → décide le popup +
  // le décompte « j'ai répondu ».
  const userAnsweredRef = useRef<Set<number>>(new Set());
  // Instant (horloge serveur) où j'ai répondu à une étape → mon rang parmi les bots.
  const answerAtRef = useRef<Record<number, number>>({});
  const serverNowRef = useRef(serverNow);
  useEffect(() => {
    serverNowRef.current = serverNow;
  }, [serverNow]);
  // Feedback « gamy » (popup) : série + XP cumulé, comme le player V2.
  const streakRef = useRef(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [pendingFeedback, setPendingFeedback] = useState<LiveFeedback | null>(null);
  const dismissFeedback = useCallback(() => setPendingFeedback(null), []);
  // Popup de fin de chasse aux indices (XP glané, client-only comme la V2).
  const [clueFeedback, setClueFeedback] = useState<LiveClueFeedback | null>(null);
  const registerClueComplete = useCallback((xp: number) => {
    setSessionXp((x) => x + xp);
    setClueFeedback({ xp });
  }, []);
  const dismissClueFeedback = useCallback(() => setClueFeedback(null), []);
  // Dernière position connue de l'élève PAR étape (figée pour la popup), captée
  // pendant que l'étape est courante (avant qu'elle ne devienne passée).
  const myStepPositionRef = useRef<Record<number, { position: number; total: number }>>({});
  const submitMutation = useMutation({
    mutationFn: async (vars: { stepIndex: number; blockIds: string[] }) => {
      const { data: r, error } = await supabase.functions.invoke<{
        was_correct: boolean;
        points: number;
        eligible: boolean;
        correct_block_id: string | null;
        correct_block_ids?: string[];
        /** Étape déjà répondue : réponse ENREGISTRÉE (1re réponse gagnante). */
        already_answered?: boolean;
        chosen_block_id?: string | null;
        chosen_block_ids?: string[];
        /** Défi privé : nouvelles durées si la question finit plus tôt. */
        step_durations_ms?: number[] | null;
      }>('submit-live-answer', {
        body: {
          challenge_id: challengeId,
          step_index: vars.stepIndex,
          // Choix primaire (mode single) + ensemble coché (mode multiple).
          chosen_block_id: vars.blockIds[0] ?? null,
          chosen_block_ids: vars.blockIds,
          test: testMode,
          session: sessionLock.sessionId,
        },
      });
      if (error) {
        if (await isSupersededError(error)) sessionLock.markSuperseded();
        throw error;
      }
      return { ...vars, r: r! };
    },
    onSuccess: ({ stepIndex: idx, blockIds, r }) => {
      // On STOCKE le résultat ; l'effet de révélation (ci-dessous) décide QUAND
      // révéler la bonne réponse + déclencher la popup (résolution de l'étape).
      // Étape déjà répondue (retour sur la page) : c'est la réponse ENREGISTRÉE
      // qui compte, pas celle qu'on vient d'envoyer.
      const chosen = r.already_answered ? (r.chosen_block_ids ?? []) : blockIds;
      // Live de programme : HORA créditée à la carte de la question.
      if (!r.already_answered && r.points > 0) publishProgress('live-answered');
      if (r.already_answered) setSelected((prev) => ({ ...prev, [idx]: chosen }));
      // Dernier à répondre : la question finit plus tôt, on relit l'état.
      if (r.step_durations_ms) void stateQuery.refetch();
      setResults((prev) => ({
        ...prev,
        [idx]: {
          stepIndex: idx,
          wasCorrect: r.was_correct,
          points: r.points,
          eligible: r.eligible,
          correctBlockId: r.correct_block_id,
          correctBlockIds: r.correct_block_ids ?? (r.correct_block_id ? [r.correct_block_id] : []),
          chosenBlockId: r.already_answered
            ? (r.chosen_block_id ?? chosen[0] ?? null)
            : (blockIds[0] ?? null),
          chosenBlockIds: chosen,
        },
      }));
    },
    onError: (err) => {
      // Ne pas avaler silencieusement un échec (sinon corrigé jamais affiché).
      console.error('[live] submit-live-answer', err);
    },
  });

  const submit = useCallback(
    (chosen: string | string[] | null) => {
      // On ne bloque PAS sur `submitMutation.isPending` (mutation partagée → une
      // soumission lente bloquerait l'étape suivante) : la garde par étape suffit.
      if (stepIndex < 0 || answeredRef.current.has(stepIndex)) return;
      // Pause « résultats » : le temps de réponse est écoulé.
      if (answerTimeOverRef.current) return;
      // Fenêtre coupée par une autre : elle ne répond plus.
      if (sessionLock.superseded) return;
      const blockIds = chosen == null ? [] : Array.isArray(chosen) ? chosen : [chosen];
      answeredRef.current.add(stepIndex);
      if (blockIds.length > 0) {
        // Réponse réelle de l'élève (≠ blanc auto) : popup + décompte + rang.
        userAnsweredRef.current.add(stepIndex);
        answerAtRef.current[stepIndex] = serverNowRef.current;
      }
      // Feedback immédiat : la sélection est posée AVANT la réponse serveur.
      setSelected((prev) => ({ ...prev, [stepIndex]: blockIds }));
      submitMutation.mutate({ stepIndex, blockIds });
    },
    [stepIndex, submitMutation, sessionLock.superseded],
  );

  // ── Reprise : réponses déjà données avant de quitter la page ────────────────
  // (live-join en réel, live-test start en mode test). Appliquée une fois par
  // réponse : jamais de nouvel envoi, pas de popup rejouée pour les questions
  // passées ; la question en cours garde sa popup à la résolution.
  const testAnswers = testMode ? data?.answers : undefined;
  useEffect(() => {
    const answers = resumeAnswers ?? testAnswers;
    if (!answers?.length) return;
    const plan = planLiveResume(answers, stepIndex, answeredRef.current);
    if (plan.answered.length === 0) return;
    for (const i of plan.answered) answeredRef.current.add(i);
    if (plan.currentAnswered) {
      userAnsweredRef.current.add(stepIndex);
      answerAtRef.current[stepIndex] = serverNowRef.current;
    }
    setResults((prev) => ({ ...plan.results, ...prev }));
    setSelected((prev) => {
      const next = { ...prev };
      for (const [k, r] of Object.entries(plan.results)) {
        if (!next[Number(k)]) next[Number(k)] = r.chosenBlockIds;
      }
      return next;
    });
  }, [resumeAnswers, testAnswers, stepIndex]);

  // ── Participation collective de l'étape courante ────────────────────────────
  // Réel : poll la RPC `get_live_step_participation`. Test : simulation de bots.
  const realPart = useLiveStepParticipation({
    challengeId,
    stepIndex,
    enabled: !testMode && status === 'LIVE',
  });

  const currentStepMs = stepDurationAt(Math.max(0, stepIndex), baseStepMs, stepDurationsMs);
  const stepStartMs =
    status === 'LIVE' ? questionStartsAt + stepOffsetMs(stepIndex, baseStepMs, stepDurationsMs) : 0;
  const elapsedMs = status === 'LIVE' ? Math.max(0, serverNow - stepStartMs) : 0;

  // Offsets des bots figés PAR étape (mode test) — générés une fois.
  const botOffsetsRef = useRef<Record<number, number[]>>({});
  if (testMode && status === 'LIVE' && stepIndex >= 0 && !botOffsetsRef.current[stepIndex]) {
    botOffsetsRef.current[stepIndex] = planBotOffsets(LIVE_BOT_COUNT, currentStepMs);
  }

  const stepParticipation = useMemo<StepParticipation | null>(() => {
    if (status !== 'LIVE' || stepIndex < 0) return null;
    if (testMode) {
      const offsets = botOffsetsRef.current[stepIndex] ?? [];
      const userAnswered = userAnsweredRef.current.has(stepIndex);
      const botsAnswered = countBotsAnswered(offsets, elapsedMs);
      const answerAt = answerAtRef.current[stepIndex];
      const answerElapsed = answerAt != null ? Math.max(0, answerAt - stepStartMs) : elapsedMs;
      return {
        total: LIVE_BOT_COUNT + 1,
        answered: Math.min(LIVE_BOT_COUNT + 1, botsAnswered + (userAnswered ? 1 : 0)),
        myPosition: userAnswered ? positionAmong(offsets, answerElapsed) : null,
      };
    }
    return realPart.data ?? null;
  }, [status, stepIndex, testMode, elapsedMs, stepStartMs, realPart.data]);

  // ── Résolution de l'étape courante : corrigé + popup révélés dès que… ────────
  // (a) TOUT LE MONDE a répondu (résolution anticipée), OU
  // (b) le temps de réponse est écoulé (début de la pause « résultats »), OU
  // (c) la fin du chrono (gérée plus bas par l'avance de step).
  const [resolvedSteps, setResolvedSteps] = useState<Set<number>>(new Set());
  const allAnswered =
    !!stepParticipation &&
    stepParticipation.total > 0 &&
    stepParticipation.answered >= stepParticipation.total;
  const answerTimeOver =
    status === 'LIVE' &&
    stepIndex >= 0 &&
    resultPauseMs > 0 &&
    (live?.timeLeftMs ?? 0) <= resultPauseMs;
  answerTimeOverRef.current = answerTimeOver;
  // Défi privé : tout le monde a répondu → la question finit plus tôt côté
  // serveur ; on relit l'état (une fois par question) pour avancer avec lui.
  const ffRefetchedRef = useRef<Set<number>>(new Set());
  const refetchState = stateQuery.refetch;
  useEffect(() => {
    if (!isPrivate || !allAnswered || stepIndex < 0) return;
    if (ffRefetchedRef.current.has(stepIndex)) return;
    ffRefetchedRef.current.add(stepIndex);
    const t = setTimeout(() => void refetchState(), 600);
    return () => clearTimeout(t);
  }, [isPrivate, allAnswered, stepIndex, refetchState]);
  useEffect(() => {
    if (status === 'LIVE' && stepIndex >= 0 && (allAnswered || answerTimeOver)) {
      setResolvedSteps((prev) => (prev.has(stepIndex) ? prev : new Set(prev).add(stepIndex)));
    }
  }, [status, stepIndex, allAnswered, answerTimeOver]);

  // La question suivante arrive : la popup de la précédente se ferme seule.
  useEffect(() => {
    if (status !== 'LIVE' || stepIndex < 0) return;
    setPendingFeedback((fb) =>
      fb && fb.stepIndex != null && fb.stepIndex < stepIndex ? null : fb,
    );
  }, [status, stepIndex]);

  // Capture ma position sur l'étape COURANTE (podium de la question) → figée pour
  // la popup, qui peut se déclencher quand l'étape n'est plus courante.
  useEffect(() => {
    if (status === 'LIVE' && stepIndex >= 0 && stepParticipation?.myPosition != null) {
      myStepPositionRef.current[stepIndex] = {
        position: stepParticipation.myPosition,
        total: stepParticipation.total,
      };
    }
  }, [status, stepIndex, stepParticipation]);

  // ── Révélation + popup à la RÉSOLUTION de chaque étape (chrono OU tous répondu)
  // Une étape est « done » si son chrono est passé (i < stepIndex) OU si elle est
  // résolue en avance (resolvedSteps). On parcourt les étapes done dans l'ordre :
  // - répondue par l'élève → on ATTEND son résultat serveur puis on popup ;
  // - sautée (blanc) → on soumet un blanc (récupère le corrigé), pas de popup.
  const [poppedUpTo, setPoppedUpTo] = useState(-1);
  // Défi DÉJÀ fini quand la page s'ouvre (écran de fin rouvert, 29/10/2026) : cette fenêtre n'a
  // jamais pris la main (live-join et verrou de fenêtre sont sautés une fois le défi fini), donc
  // submit-live-answer refusait les « blancs » qui rapportent le corrigé (409 superseded) → plus
  // aucune correction, « Correction… » sans fin et 0 bonne réponse affichée. On prend la main une
  // fois, puis on envoie : le serveur renvoie la réponse ENREGISTRÉE et son corrigé.
  const finishedClaimRef = useRef<Promise<unknown> | null>(null);
  const takeOver = sessionLock.takeOver;
  const sendBlank = useCallback(
    (i: number) => {
      const send = () => submitMutation.mutate({ stepIndex: i, blockIds: [] });
      if (status !== 'FINISHED' || testMode) return send();
      finishedClaimRef.current ??= takeOver();
      void finishedClaimRef.current.then(send, send);
    },
    [status, testMode, takeOver, submitMutation],
  );
  useEffect(() => {
    const boundary =
      status === 'FINISHED'
        ? stepCount
        : stepIndex >= 0
          ? stepIndex + (resolvedSteps.has(stepIndex) ? 1 : 0)
          : 0;
    if (boundary <= poppedUpTo + 1) return;
    let cursor = poppedUpTo;
    for (let i = poppedUpTo + 1; i < boundary; i++) {
      if (userAnsweredRef.current.has(i)) {
        const res = results[i];
        if (!res) break; // attend le résultat serveur avant de popper (et d'avancer)
        const reward = computeXpReward(res.wasCorrect, streakRef.current);
        streakRef.current = reward.streak;
        setSessionXp((x) => x + reward.total);
        const pos = myStepPositionRef.current[i];
        setPendingFeedback({
          wasCorrect: res.wasCorrect,
          reward,
          stepPosition: pos?.position ?? null,
          stepTotal: pos?.total ?? 0,
          stepIndex: i,
        });
      } else {
        // Étape NON répondue → blanc auto pour RÉVÉLER le corrigé (0 pt, hors
        // délai). Le corrigé est serveur-only (anti-triche) : sans ça le client
        // ne pourrait pas afficher la bonne réponse d'un skip.
        if (!answeredRef.current.has(i)) {
          answeredRef.current.add(i);
          sendBlank(i);
        }
        streakRef.current = 0; // série cassée (blanc)
      }
      cursor = i;
    }
    if (cursor !== poppedUpTo) setPoppedUpTo(cursor);
  }, [status, stepIndex, stepCount, resolvedSteps, results, poppedUpTo, sendBlank]);

  const totalScore = useMemo(
    () => Object.values(results).reduce((s, r) => s + r.points, 0),
    [results],
  );
  const correctCount = useMemo(
    () => Object.values(results).filter((r) => r.wasCorrect).length,
    [results],
  );

  // Liste accumulée des étapes révélées (0..courante) + leur état d'affichage.
  const revealedSteps = useMemo<RevealedStep[]>(() => {
    if (lastShownIndex < 0) return [];
    const out: RevealedStep[] = [];
    for (let i = 0; i <= lastShownIndex; i++) {
      const s = revealed[i];
      if (!s) continue;
      const chosenSet = selected[i] ?? results[i]?.chosenBlockIds ?? [];
      out.push({
        index: i,
        step: s,
        result: results[i] ?? null,
        chosenBlockId: chosenSet[0] ?? results[i]?.chosenBlockId ?? null,
        chosenBlockIds: chosenSet,
        isCurrent: i === stepIndex,
        // Corrigé révélé : chrono passé (étape antérieure) OU résolue en avance.
        revealed: i < stepIndex || stepIndex < 0 || resolvedSteps.has(i),
      });
    }
    return out;
  }, [revealed, results, selected, stepIndex, lastShownIndex, resolvedSteps]);

  // Dernière étape résolue (répondue par tous / forcée) → on N'ATTEND PAS la fin
  // du chrono : il n'y a plus de question suivante, l'attente serait inutile.
  const lastStepResolved =
    stepCount > 0 && stepIndex === stepCount - 1 && resolvedSteps.has(stepIndex);

  let phase: LivePhase = 'loading';
  if (stateQuery.isError) phase = 'error';
  else if (stateQuery.isSuccess && !challengeId) phase = 'no-challenge';
  else if (beforeStart) phase = 'waiting';
  else if (isReading) phase = 'reading';
  else if (status === 'LIVE') phase = lastStepResolved ? 'finished' : 'live';
  else if (status === 'FINISHED') phase = 'finished';

  // Temps restant affiché selon la phase : avant le départ / lecture / étape.
  const timeLeftMs =
    phase === 'waiting'
      ? Math.max(0, startsAtMs - serverNow)
      : phase === 'reading'
        ? Math.max(0, questionStartsAt - serverNow)
        : Math.max(0, (live?.timeLeftMs ?? 0) - resultPauseMs);

  const commonStatement = payload?.statement ?? [];

  return {
    phase,
    joinRefused,
    challengeId,
    title: data?.title ?? null,
    stepCount,
    stepDurationMs: Math.max(1, currentStepMs - resultPauseMs),
    stepIndex,
    timeLeftMs,
    currentStep: stepIndex >= 0 ? (stepQuery.data ?? null) : null,
    revealedSteps,
    commonStatement,
    coverUrl: data?.cover_url ?? null,
    pendingFeedback,
    clueFeedback,
    sessionXp,
    dismissFeedback,
    registerClueComplete,
    dismissClueFeedback,
    stepLoading: stepQuery.isFetching && !stepQuery.data,
    joinedStep: participation?.joinedStep ?? 0,
    eligible: participation?.eligible ?? true,
    currentResult: stepIndex >= 0 ? (results[stepIndex] ?? null) : null,
    stepParticipation,
    currentResolved: stepIndex >= 0 && resolvedSteps.has(stepIndex),
    totalScore,
    correctCount,
    submit,
    submitting: submitMutation.isPending,
    refetchState: stateQuery.refetch,
    scoring: data?.scoring === 'hora' ? 'hora' : 'points',
    prize: data?.prize ?? null,
    ranked: participation?.ranked ?? true,
    privateInfo: data?.private ?? null,
    serverNow,
    superseded: sessionLock.superseded,
    takeOver: sessionLock.takeOver,
  };
}
